import type {
  ApiError,
  ChangeEmailRequest,
  ChangeEmailResponse,
  ChangePasswordRequest,
  PopulateUserRequest,
  ResetPasswordConfirmRequest,
  ResetPasswordResponse
} from "@9amhealth/openapi";
import {
  LillyControllerService,
  LoginRequest,
  Saml2AuthControllerService,
  SoleraControllerService,
  UserControllerService
} from "@9amhealth/openapi";
import { DbFileService } from "src/lib/db/DbFileService";
import { onAppActive } from "src/hybrid/appLifecycle";
import { Network } from "@capacitor/network";
import { Blac, Cubit } from "blac-next";
import { processRequestQueue } from "src/api/ApiController";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { logSentryEvent } from "src/lib/logSentryEvent";
import parseJwt from "src/lib/parseJwt";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { reportAuthError } from "src/lib/reportAuthError";
import LocalStorageTokenCredentialsProvider from "src/state/UserCubit/CredentialProviders/LocalStorageTokenCredentialsProvider";
import type { ProviderCredentials } from "src/state/UserCubit/CredentialsProvider";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import Glp1RefillBloc from "src/ui/components/Gpl1RefillForm/Glp1RefillBloc";
import { AuthenticationMethod } from "src/ui/components/WithAuth/WithAuthCubit";
import { globalEvents } from "../../constants/globalEvents";
import type { AuthStage } from "../../types/auth";
import LoadingCubit, { LoadingKey } from "../LoadingCubit/LoadingCubit";
import AppAccessCubit, {
  AppAccessStatus
} from "../AppAccessCubit/AppAccessCubit";
import QuestionnaireCubit from "../QuestionnaireCubit/QuestionnaireCubit";
import {
  apiMiddleware,
  biometricAuthState,
  healthSyncState,
  pushNotificationState,
  subscriptionState,
  tracker,
  userEmailVerificationState,
  userState
} from "../state";
import { StorageController } from "../StorageBloc/StorageBloc";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "../UserPreferencesCubit/UserPreferencesCubit";
import WebSocketBloc from "../WebSocketBloc/WebSocketBloc";
import { MAX_TIMEOUT } from "src/constants/misc";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import { ErrorCode } from "src/constants/errorCodes";
import AppViewCubit from "../AppViewCubit/AppViewCubit";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

export type AuthenticationStatus =
  | "authenticated"
  | "pending"
  | "unauthenticated";

type AuthenticationBlocState = {
  authenticationStatus: AuthenticationStatus;
  credentials: ProviderCredentials;
  authStage: AuthStage;
  // Storage read only - settles almost immediately.
  credentialsRestored: boolean;
  // The whole initial restore attempt - storage read plus re-establishing the
  // session from it. This is what decides login-vs-app.
  sessionRestoreSettled: boolean;
  // Network access check - settles later, never gates the app shell.
  accessResolved: boolean;
  hasStoredCredentials: boolean;
};

/**
 * Ceiling on the one call the deferred-bootstrap path still awaits. It exists so
 * that path can never recreate the stall it was added to fix.
 */
const DEFERRED_AUTH_STAGE_TIMEOUT_MS = 8000;

const websocketState = Blac.getBloc(WebSocketBloc);

export default class AuthenticationBloc extends Cubit<AuthenticationBlocState> {
  static keepAlive = true;
  externalLoginInProgress = false;
  private selectedUserId: string | null = null;

  // Token provider - always uses localStorage
  // Biometric is purely for app locking, not credential storage
  tokenProvider = new LocalStorageTokenCredentialsProvider();

  // Inflight request deduplication
  private initialCredentialsRequest?: Promise<void>;
  private createAnonymousAccountRequest?: Promise<void>;
  private loginRequest?: Promise<void>;
  private updateAuthStageRequest?: Promise<void>;
  private static refreshAccessTokenRequest?: Promise<void>;
  private bootstrapGeneration = 0;

  constructor() {
    super({
      authenticationStatus: "unauthenticated",
      credentials: {},
      authStage: "nullAuth",
      credentialsRestored: false,
      sessionRestoreSettled: false,
      accessResolved: false,
      hasStoredCredentials: false
    });

    // Always load credentials from storage - the handover/token flows
    // will handle their own authentication after the base session is established
    this.selectUserIdFromStorage();

    // Set hasStoredCredentials synchronously so biometric lock can show immediately
    const hasStoredCredentials = this.selectedUserId !== null;
    this.emit({ ...this.state, hasStoredCredentials });

    // Seed the optimistic shell from the persisted access flag before anything
    // touches the network, so a returning member renders the app immediately.
    if (hasStoredCredentials && StorageController.getHasAppAccess()) {
      Blac.getBloc(AppAccessCubit).seedGrantedFromCache();
    }

    // getCredentials flips credentialsRestored as soon as it has read storage;
    // the restore attempt is only over once the whole call has settled.
    this.initialCredentialsRequest = this.getCredentials().finally(() => {
      this.markSessionRestoreSettled();
    });

    this.listenAppStatus();
    this.listenNetworkStatus();

    // The shared response cache is keyed by user, but a session reset also has
    // to invalidate what the outgoing user already has in flight.
    onUserSessionReset(apiMiddleware.clearAll);

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.log("USER_CLEAR");
      this.bootstrapGeneration++;
      this.emit({
        authenticationStatus: "unauthenticated",
        credentials: {},
        authStage: "nullAuth",
        credentialsRestored: this.state.credentialsRestored,
        sessionRestoreSettled: this.state.sessionRestoreSettled,
        accessResolved: false,
        hasStoredCredentials: false
      });
    });

    window.addEventListener(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      globalEvents.SET_CREDENTIALS as any,
      (event: CustomEvent<ProviderCredentials & { testMode?: boolean }>) => {
        this.log("SET_CREDENTIALS", { hasCredentials: Boolean(event.detail) });

        // Only handle external credential injection (with payload)
        // Internal dispatches for state clearing don't have credentials
        if (!event.detail) {
          this.log(
            "SET_CREDENTIALS: no credentials provided, skipping storage clear"
          );
          return;
        }

        const { testMode, ...credentials } = event.detail;

        // Test mode: only update in-memory credentials without storage clear or reload
        // Used for E2E tests to simulate token expiration
        if (testMode) {
          this.log(
            "SET_CREDENTIALS: test mode - updating credentials core only"
          );
          void this.updateCredentialsCore(credentials);
          return;
        }

        // Production mode: full reset with storage clear and reload
        localStorage.clear();
        sessionStorage.clear();
        void this.setCredentials(credentials).then(() => {
          window.location.reload();
        });
      }
    );
  }

  get accessToken(): string | undefined {
    return this.state.credentials.accessToken;
  }

  get refreshToken(): string | undefined {
    return this.state.credentials.refreshToken;
  }

  get authenticationStatus() {
    return this.state.authenticationStatus;
  }

  isAccessTokenExpired = (): boolean => {
    if (!this.accessToken) {
      return true;
    }
    const parsed = parseJwt(this.accessToken);
    return parsed.expired;
  };

  extractUserId = (): string | undefined => {
    const parsedAccessToken = parseJwt(this.accessToken);
    return parsedAccessToken.sub;
  };

  get activeUserId(): string | null {
    // Always prioritize the user from access token if available
    const tokenUserId = this.extractUserId();
    if (tokenUserId) {
      this.log("activeUserId from token", { tokenUserId });
      return tokenUserId;
    }

    // Only use selected user if no valid access token
    this.log("activeUserId from selected", {
      selectedUserId: this.selectedUserId
    });
    return this.selectedUserId;
  }

  private categorizeAuthStage(authStage: string | null): AuthStage {
    switch (authStage) {
      case "fullAuth":
      case "partialAuth":
      case "anonymous":
      case "noAuth":
        return authStage;
      default:
        return "nullAuth";
    }
  }

  private validateAndParseRefreshToken(
    token: string,
    userId: string
  ): { sub: string } | null {
    try {
      const parsedToken = parseJwt(token);
      if (!parsedToken.sub) {
        this.log("Invalid or missing JWT subject for user", userId);
        return null;
      }
      return { sub: parsedToken.sub };
    } catch (error) {
      this.log("Failed to parse JWT for user", { userId, error });
      reportErrorSentry(
        new Error(`JWT parsing failed for user ${userId}`, { cause: error })
      );
      return null;
    }
  }

  private findBestUserCandidate(
    latestWithAccess: { userId: string; time: number; storage?: Storage },
    latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    >
  ): { userId: string; storage: Storage } | null {
    if (latestWithAccess.userId && latestWithAccess.storage) {
      return {
        userId: latestWithAccess.userId,
        storage: latestWithAccess.storage
      };
    }

    const authStagePriority: AuthStage[] = [
      "fullAuth",
      "partialAuth",
      "anonymous",
      "noAuth",
      "nullAuth"
    ];

    for (const stage of authStagePriority) {
      if (latestByAuthStage[stage].userId && latestByAuthStage[stage].storage) {
        return {
          userId: latestByAuthStage[stage].userId,
          storage: latestByAuthStage[stage].storage
        };
      }
    }

    return null;
  }

  private processUserCandidate(
    lastActiveKey: string,
    latestWithAccess: { userId: string; time: number; storage?: Storage },
    latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    >
  ): void {
    const storageType =
      localStorage.getItem(lastActiveKey) !== null
        ? localStorage
        : sessionStorage;
    const activeTime = parseInt(storageType.getItem(lastActiveKey) ?? "0", 10);
    const userIdRegex = StorageController.createPatternForKey("lastActive");
    const userId = lastActiveKey.match(userIdRegex)?.[1];

    this.log("Processing user candidate", {
      lastActiveKey,
      storageType:
        storageType === localStorage ? "localStorage" : "sessionStorage",
      activeTime,
      userId
    });

    if (!userId) {
      this.log("No userId found in key", { lastActiveKey });
      return;
    }

    // Check for refresh token in the same storage where lastActive was found
    const hasRefreshToken = storageType.getItem(`9am-${userId}-refreshToken`);
    if (!hasRefreshToken) {
      this.log("No refresh token for user in expected storage", {
        userId,
        expectedStorage:
          storageType === localStorage ? "localStorage" : "sessionStorage"
      });
      return;
    }

    const parsedRefreshToken = this.validateAndParseRefreshToken(
      hasRefreshToken,
      userId
    );
    if (!parsedRefreshToken) {
      this.log("Invalid refresh token for user", { userId });
      return;
    }

    // Check for app access in the same storage where lastActive was found
    const hasAppAccess =
      storageType.getItem(`9am-${userId}-hasAppAccess`) === "true";

    this.log("User candidate evaluation", {
      userId: parsedRefreshToken.sub,
      hasAppAccess,
      activeTime,
      storageType:
        storageType === localStorage ? "localStorage" : "sessionStorage"
    });

    if (hasAppAccess) {
      if (activeTime > latestWithAccess.time) {
        this.log("New best user with app access", {
          previousUserId: latestWithAccess.userId,
          newUserId: parsedRefreshToken.sub,
          previousTime: latestWithAccess.time,
          newTime: activeTime
        });
        latestWithAccess.userId = parsedRefreshToken.sub;
        latestWithAccess.time = activeTime;
        latestWithAccess.storage = storageType;
      }
    } else {
      // Check for auth stage in the same storage where lastActive was found
      const authStage = storageType.getItem(`9am-${userId}-authStage`);
      const category = this.categorizeAuthStage(authStage);

      this.log("User auth stage categorization", {
        userId: parsedRefreshToken.sub,
        authStage,
        category,
        storageType:
          storageType === localStorage ? "localStorage" : "sessionStorage"
      });

      if (activeTime > latestByAuthStage[category].time) {
        this.log("New best user for auth stage", {
          category,
          previousUserId: latestByAuthStage[category].userId,
          newUserId: parsedRefreshToken.sub,
          previousTime: latestByAuthStage[category].time,
          newTime: activeTime
        });
        latestByAuthStage[category].userId = parsedRefreshToken.sub;
        latestByAuthStage[category].time = activeTime;
        latestByAuthStage[category].storage = storageType;
      }
    }
  }

  selectBestUserFromStorage(): { userId: string; storage: Storage } | null {
    this.log("selectBestUserFromStorage - starting search");
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    const allStorageKeys = [...localStorageKeys, ...sessionStorageKeys];
    const userIdRegexLastActive =
      StorageController.createPatternForKey("lastActive");
    const lastActivekeys = allStorageKeys.filter((k) =>
      k.match(userIdRegexLastActive)
    );

    this.log("Found lastActive keys", {
      totalKeys: allStorageKeys.length,
      lastActiveKeys: lastActivekeys.length,
      keys: lastActivekeys
    });

    const latestWithAccess = {
      userId: "",
      time: 0,
      storage: undefined as Storage | undefined
    };
    const latestByAuthStage: Record<
      AuthStage,
      { userId: string; time: number; storage?: Storage }
    > = {
      fullAuth: { userId: "", time: 0, storage: undefined },
      partialAuth: { userId: "", time: 0, storage: undefined },
      anonymous: { userId: "", time: 0, storage: undefined },
      noAuth: { userId: "", time: 0, storage: undefined },
      nullAuth: { userId: "", time: 0, storage: undefined }
    };

    for (const lastActiveKey of lastActivekeys) {
      this.processUserCandidate(
        lastActiveKey,
        latestWithAccess,
        latestByAuthStage
      );
    }

    const result = this.findBestUserCandidate(
      latestWithAccess,
      latestByAuthStage
    );
    this.log("selectBestUserFromStorage - result", {
      result,
      latestWithAccess,
      latestByAuthStage
    });
    return result;
  }

  getUserIdFromStoredRefreshToken(): string | null {
    this.log("getUserIdFromStoredRefreshToken");
    const userIdRegexRefreshToken =
      StorageController.createPatternForKey("refreshToken");
    const allStorageKeys = Object.keys(localStorage);
    const refreshKeys = allStorageKeys.filter((k) =>
      k.match(userIdRegexRefreshToken)
    );
    if (refreshKeys.length > 0) {
      const userId = refreshKeys[0].match(userIdRegexRefreshToken)?.[1];
      if (userId) {
        return userId;
      }
    }

    return null;
  }

  selectUserIdFromStorage(): void {
    this.log("selectUserIdFromStorage");

    // Check if StorageController already has an active user from early initialization
    if (StorageController.activeUserId) {
      this.log("StorageController already has active user", {
        activeUserId: StorageController.activeUserId
      });
      this.selectedUserId = StorageController.activeUserId;
      return;
    }

    // Use StorageController's unified user selection
    const selectedUserId = StorageController.selectAndActivateBestUser();

    if (selectedUserId) {
      this.log("Selected user from StorageController", { selectedUserId });
      this.selectedUserId = selectedUserId;
    } else {
      this.log("No user found in storage");
      this.selectedUserId = null;
    }

    // Update the storage active user ID (in case it wasn't set)
    this.updateStorageActiveUserId();
    this.log("Selected user from storage", { selectedUserId });
  }

  private updateStorageActiveUserId(): void {
    const { activeUserId } = this;
    const previousActiveUserId = StorageController.activeUserId;
    this.log("Updating StorageController activeUserId", {
      activeUserId,
      previousActiveUserId
    });
    StorageController.setActiveUserId(activeUserId);

    // Log active time whenever a user is selected
    if (activeUserId) {
      StorageController.logActive();
      // This handles cases where storage was used before authentication
      if (previousActiveUserId === null && activeUserId) {
        this.log("Migrating temporary storage for newly authenticated user");
        StorageController.migrateTemporaryStorageToUser(activeUserId);
      }
    }
  }

  get loggingEnabled(): boolean {
    return featureFlags.getFlag(FeatureFlagName.loggingAuthenticationManager);
  }

  private readonly log = (message: string, etc?: unknown): void => {
    if (this.loggingEnabled) {
      console.warn(`[CRED]: ${message}`, etc ?? "");
    }
    logSentryBreadcrumb("credentialProvider", [message, etc]);
  };

  private isNetworkError(error: unknown): boolean {
    if (error instanceof TypeError) {
      const message = error.message.toLowerCase();
      const networkKeywords = [
        "fetch",
        "network",
        "internet",
        "offline",
        "load failed"
      ];
      return networkKeywords.some((keyword) => message.includes(keyword));
    }
    return false;
  }

  /**
   * Re-evaluate WebSocket connection based on current state
   * Connects WebSocket only if:
   * 1. Has access token AND
   * 2. Has app access (determined by AppAccessCubit)
   */
  recheckWebSocketConnection = async (): Promise<void> => {
    const { accessToken } = this;

    if (!accessToken) {
      websocketState.disconnect();
      return;
    }

    const appAccessState = Blac.getBloc(AppAccessCubit);
    const hasAppAccess =
      appAccessState.state.status === AppAccessStatus.GRANTED;

    this.log("WebSocket connection decision", {
      hasAccessToken: Boolean(accessToken),
      hasAppAccess,
      appAccessStatus: appAccessState.state.status
    });

    if (accessToken && hasAppAccess) {
      void websocketState.connectAndAuthenticate(accessToken);
    } else {
      websocketState.disconnect();
    }
  };

  listenAppStatus = () => {
    onAppActive(() => {
      void this.getCredentials();
    });
  };

  private wasOffline = false;

  listenNetworkStatus = () => {
    void Network.addListener("networkStatusChange", (status) => {
      this.log("networkStatusChange", status);

      if (status.connected && this.wasOffline) {
        this.log("Network restored - checking credentials");
        void this.getCredentials();
      }

      this.wasOffline = !status.connected;
    });

    // Initialize wasOffline state
    void Network.getStatus().then((status) => {
      this.wasOffline = !status.connected;
    });
  };

  private getCredentialsInProgress?: Promise<void>;

  async getCredentials(): Promise<void> {
    // Deduplicate concurrent getCredentials calls to prevent duplicate biometric prompts
    if (this.getCredentialsInProgress) {
      this.log(
        "getCredentials: already in progress — reusing existing promise"
      );
      return this.getCredentialsInProgress;
    }

    this.getCredentialsInProgress = this.getCredentialsInternal();
    try {
      await this.getCredentialsInProgress;
    } finally {
      this.getCredentialsInProgress = undefined;
    }
  }

  private async getCredentialsInternal(): Promise<void> {
    if (this.externalLoginInProgress) {
      this.log("getCredentials: external login in progress — skipping");
      return;
    }
    this.log("getCredentials started");

    // Always use token-based auth from localStorage
    // Biometric lock is handled at the UI layer (WithAuth -> LockedScreen)
    // By the time we get here, biometric verification (if enabled) has already passed
    let tokenCredentials: ProviderCredentials | undefined;

    try {
      tokenCredentials = await this.tokenProvider.getCredentials();
    } catch (error) {
      reportErrorSentry(error);
    }

    // The storage read is done, which is all this flag promises. Everything
    // below is network work, and WithAuthCubit reports that separately through
    // accessResolved — blocking the shell on it here would undo the
    // optimistic boot for anyone with an expired access token.
    this.markCredentialsRestored();

    if (tokenCredentials) {
      await this.tryTokenBasedAuth(tokenCredentials);
    }
  }

  private markCredentialsRestored(): void {
    if (this.state.credentialsRestored) {
      return;
    }
    this.emit({ ...this.state, credentialsRestored: true });
  }

  /**
   * The initial restore attempt is over: storage has been read and any tokens
   * found in it have been used to re-establish the session, or given up on.
   * Whatever `authStage` says from here on is an answer, not a not-yet — which
   * is what keeps a returning member from being shown the login overlay for
   * the moment between the storage read and the resolved stage.
   */
  private markSessionRestoreSettled(): void {
    if (this.state.sessionRestoreSettled) {
      return;
    }
    this.emit({
      ...this.state,
      credentialsRestored: true,
      sessionRestoreSettled: true
    });
  }

  private async tryTokenBasedAuth(
    credentials: ProviderCredentials
  ): Promise<boolean> {
    // Check if tokens exist and are not expired
    const parsedRefreshToken = parseJwt(credentials.refreshToken);
    const parsedAccessToken = parseJwt(credentials.accessToken);

    const hasRefreshToken =
      Boolean(credentials.refreshToken) && !parsedRefreshToken.expired;
    const hasValidAccessToken =
      Boolean(credentials.accessToken) && !parsedAccessToken.expired;

    this.log("tryTokenBasedAuth", {
      hasRefreshToken,
      hasValidAccessToken,
      refreshTokenExpired: parsedRefreshToken.expired,
      accessTokenExpired: parsedAccessToken.expired
    });

    if (!hasRefreshToken && !hasValidAccessToken) {
      this.log("tryTokenBasedAuth: no valid tokens (expired or missing)");
      return false;
    }

    if (this.externalLoginInProgress) {
      this.log("tryTokenBasedAuth: external login in progress, aborting");
      return false;
    }

    // If we have a valid (unexpired) access token, use it directly
    if (hasValidAccessToken) {
      this.log("tryTokenBasedAuth: using valid access token");
      await this.setCredentials(credentials);
      return true;
    }

    // We have a refresh token but no valid access token - need to refresh
    if (hasRefreshToken) {
      this.log("tryTokenBasedAuth: access token expired, refreshing");

      // First set the refresh token so refreshAccessToken can use it
      await this.setCredentials({
        refreshToken: credentials.refreshToken
      });

      try {
        await this.refreshAccessToken();
        // After successful refresh, trigger side effects
        if (this.accessToken) {
          await this.setCredentials({
            accessToken: this.accessToken,
            refreshToken: this.refreshToken
          });
          return true;
        }
      } catch (error) {
        this.log("tryTokenBasedAuth: token refresh failed", { error });
        return false;
      }
    }

    return false;
  }

  handleTokenRefreshFailure = (): void => {
    BlockingLoadingOverlayController.endAllLoadings();
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.finishAllLoadings();
  };

  refreshAccessToken = async (): Promise<void> => {
    if (this.externalLoginInProgress) {
      this.log("refreshAccessToken: external login in progress — skipping");
      return Promise.resolve();
    }
    if (AuthenticationBloc.refreshAccessTokenRequest) {
      this.log("refreshAccessToken: already running");
      return AuthenticationBloc.refreshAccessTokenRequest;
    }

    const request = new Promise<void>((resolve, reject) => {
      this.log("starting refreshAccessToken");
      if (!this.refreshToken) {
        this.log("refreshAccessToken: no refresh token");
        const noTokenError = new Error("No refresh token");
        // Only delete token credentials, preserve biometric for fallback
        Promise.all([this.clearAllUserData(), this.deleteTokenCredentials()])
          .then(() => {
            this.handleTokenRefreshFailure();
            reject(noTokenError);
          })
          .catch((e: unknown) => {
            this.log("Error during cleanup when no refresh token", e);
            reject(noTokenError);
          });
        return;
      }

      UserControllerService.refreshToken()
        .then(async (response) => {
          try {
            this.log(
              "refreshAccessToken: got response, updating credentials core"
            );
            await this.updateCredentialsCore({
              accessToken: response.data.accessToken,
              refreshToken: this.refreshToken
            });
            this.log(
              "refreshAccessToken: credentials updated, resolving promise"
            );
            logSentryEvent("auth.token_refresh.success");
            resolve();
          } catch (e: unknown) {
            this.log("refreshAccessToken: error updating credentials", e);
            reject(e as Error);
          }
        })
        .catch(async (e: unknown) => {
          this.log("refreshAccessToken: failed", e);

          // Only clear credentials on auth failures, not network errors
          // Network errors (offline, disconnected) should preserve credentials
          // so the user can retry when connectivity returns
          if (!this.isNetworkError(e)) {
            try {
              await this.deleteTokenCredentials();
              await this.clearAllUserData();
              this.handleTokenRefreshFailure();
            } catch (cleanupError) {
              this.log(
                "Error during cleanup after failed refresh",
                cleanupError
              );
            }
          } else {
            this.log(
              "refreshAccessToken: network error detected, preserving credentials for retry"
            );
          }

          reportAuthError("token_refresh", e, {
            step: "refresh",
            reason: this.isNetworkError(e) ? "network" : "auth"
          });
          reject(e as Error);
        })
        .finally(() => {
          AuthenticationBloc.refreshAccessTokenRequest = undefined;
        });
    });

    request.catch(() => {
      this.handleTokenRefreshFailure();
    });

    AuthenticationBloc.refreshAccessTokenRequest = request;
    return request;
  };

  private refreshTimeout?: number;
  scheduleAutoRefresh = (): void => {
    const parsedAccessToken = parseJwt(this.accessToken);
    const timeTillAuthTokenExpires = parsedAccessToken.expiresInMs;
    const prematureRefreshTime = 60 * 1000 * 1; // 1 minute
    const ttl = Math.max(timeTillAuthTokenExpires - prematureRefreshTime, 0);
    // cap timeout to 32 bit int, because setTimeout can't handle more
    const cap = Math.min(ttl, MAX_TIMEOUT);

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    this.refreshTimeout = window.setTimeout(() => {
      if (!this.externalLoginInProgress) {
        void this.refreshAccessToken();
      } else {
        this.log(
          "scheduleAutoRefresh: suppressed by external login in progress"
        );
      }
    }, cap);

    this.log("scheduleAutoRefresh", {
      prematureRefreshTime,
      cap,
      refreshesAt: new Date(Date.now() + cap),
      expiresAt: new Date(Date.now() + timeTillAuthTokenExpires),
      timeTillAuthTokenExpires
    });
  };

  /**
   * Core credential update logic - updates state and storage only.
   * Does NOT trigger any API calls or side effects that depend on credentials.
   * Use this for token refresh to avoid circular dependencies.
   */
  private async updateCredentialsCore(
    newCredentials: ProviderCredentials
  ): Promise<{
    previousUserId: string | undefined;
    newUserId: string | undefined;
    authenticationStatus: AuthenticationStatus;
  }> {
    const previousUserId = this.extractUserId();

    this.log("updateCredentialsCore - starting", {
      hasAccessToken: Boolean(newCredentials.accessToken),
      hasRefreshToken: Boolean(newCredentials.refreshToken),
      previousUserId
    });

    const credentials = {
      ...this.state.credentials,
      ...newCredentials
    };

    // Clear selected user ID when setting new credentials
    // This ensures the access token takes precedence
    if (credentials.accessToken || credentials.refreshToken) {
      // Check for and log token/selected user mismatch
      if (credentials.accessToken && this.selectedUserId) {
        const tokenUserId = parseJwt(credentials.accessToken)?.sub;
        if (tokenUserId && tokenUserId !== this.selectedUserId) {
          this.log(
            "Token user mismatch with selected user - clearing selected user",
            {
              tokenUserId,
              selectedUserId: this.selectedUserId
            }
          );
        }
      }
      this.log("Clearing selectedUserId - token takes precedence", {
        previousSelectedUserId: this.selectedUserId
      });
      this.selectedUserId = null;
    }

    let authenticationStatus: typeof this.state.authenticationStatus =
      "unauthenticated";

    if (credentials.accessToken) {
      authenticationStatus = "authenticated";
    } else if (credentials.refreshToken) {
      authenticationStatus = "pending";
    }

    this.emit({
      ...this.state,
      credentials,
      authenticationStatus
    });

    this.log("updateCredentialsCore - emitted state", {
      authenticationStatus
    });

    // Log user change for debugging
    const newUserId = this.extractUserId();
    if (newUserId !== previousUserId) {
      this.log("Authenticated user changed", { previousUserId, newUserId });
      this.updateStorageActiveUserId();
    }

    // Save credentials to localStorage
    try {
      await this.tokenProvider.setCredentials(credentials);
      if (credentials.accessToken) {
        // Process queued requests with new token
        void processRequestQueue();
      }
    } catch (error) {
      reportErrorSentry(error);
    }

    this.log("updateCredentialsCore - completed", {
      authenticationStatus,
      newUserId
    });

    return { previousUserId, newUserId, authenticationStatus };
  }

  async setCredentials(newCredentials: ProviderCredentials): Promise<void> {
    if (this.externalLoginInProgress) {
      this.log("setCredentials during external login");
    }
    const currentAccessToken = this.accessToken;

    // Capture persisted user ID BEFORE updateCredentialsCore updates it
    // This is used to detect real user changes (vs page refresh for same user)
    const persistedUserIdBeforeUpdate = StorageController.activeUserId;

    this.log("setCredentials - starting", {
      hasAccessToken: Boolean(newCredentials.accessToken),
      hasRefreshToken: Boolean(newCredentials.refreshToken),
      persistedUserIdBeforeUpdate
    });

    // Call core credential update logic (no API calls)
    const { previousUserId, newUserId, authenticationStatus } =
      await this.updateCredentialsCore(newCredentials);

    this.log("setCredentials - core update complete", {
      authenticationStatus
    });

    // Must precede the bootstrap: USER_CHANGED is what makes every cubit drop
    // the previous user's data and in-flight requests, and the bootstrap below
    // immediately starts reading that state and loading the new user's.
    this.dispatchStateChangeEvents(
      authenticationStatus,
      previousUserId ?? null,
      newUserId ?? null,
      persistedUserIdBeforeUpdate
    );

    // Started before the auth stage refresh so that its cache clear happens
    // first and both end up sharing one profile request instead of two.
    this.runDeferredBootstrap(
      authenticationStatus,
      newCredentials,
      currentAccessToken ?? null
    );

    // Credentials are persisted; the caller can move on from here.
    await this.updateAuthStageBounded();

    // Schedule auto refresh
    if (this.refreshToken) {
      this.scheduleAutoRefresh();
    }

    // Handle email/password login post-authentication
    if (
      newCredentials.username &&
      newCredentials.password &&
      authenticationStatus === "authenticated"
    ) {
      await this.handleEmailPasswordLogin();
    }
  }

  /**
   * WithAuthCubit decides App-vs-Login from `authStage`, and
   * prepareForExternalTokenLogin has just set it to "nullAuth". Navigating
   * before this resolves makes /app flash the login overlay, so login still
   * waits for it — but bounded, so one stalled request cannot strand the
   * member the way the full bootstrap fan-out could.
   */
  private async updateAuthStageBounded(): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      await Promise.race([
        this.updateAuthStage(),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, DEFERRED_AUTH_STAGE_TIMEOUT_MS);
        })
      ]);
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
    }
  }

  /**
   * The post-login data load — app access, the deferred fan-out and the
   * websocket recheck. Deliberately not awaited: it has no timeout, and
   * bootstrap completeness is not a precondition for entering the app.
   */
  private runDeferredBootstrap(
    authenticationStatus: string,
    newCredentials: ProviderCredentials,
    currentAccessToken: string | null
  ): void {
    void this.handleAuthStatusChange(
      authenticationStatus,
      newCredentials,
      currentAccessToken
    ).catch(reportErrorSentry);
  }

  private async handleAuthStatusChange(
    authenticationStatus: string,
    newCredentials: ProviderCredentials,
    currentAccessToken: string | null
  ): Promise<void> {
    const generation = ++this.bootstrapGeneration;
    const { id: currentlyLoadedUserDataId } = userState.state.userData ?? {};
    const userIdChanged = currentlyLoadedUserDataId !== this.activeUserId;

    try {
      switch (authenticationStatus) {
        case "authenticated":
          await this.handleAuthenticatedStatus(
            currentlyLoadedUserDataId,
            userIdChanged,
            newCredentials,
            currentAccessToken,
            generation
          );
          break;
        case "pending":
          websocketState.pauseSendingUntilAuthSuccess();
          break;
        case "unauthenticated":
          websocketState.disconnect();
          break;
      }
    } finally {
      if (this.isCurrentBootstrap(generation)) {
        this.emit({ ...this.state, accessResolved: true });
      }
    }
  }

  /**
   * A logout or a second login supersedes any bootstrap still in flight for the
   * previous session; its late results must not be written back onto the state
   * the new session already reset.
   */
  private isCurrentBootstrap(generation: number): boolean {
    return generation === this.bootstrapGeneration;
  }

  private async handleAuthenticatedStatus(
    currentlyLoadedUserDataId: string | undefined,
    userIdChanged: boolean,
    newCredentials: ProviderCredentials,
    currentAccessToken: string | null,
    generation: number
  ): Promise<void> {
    const appAccessState = Blac.getBloc(AppAccessCubit);
    const appViewState = Blac.getBloc(AppViewCubit);
    appViewState.setPartnerSession();

    const credentialsContainEmailPassword = Boolean(
      newCredentials.username && newCredentials.password
    );
    const shouldLoadData =
      !currentlyLoadedUserDataId ||
      userIdChanged ||
      credentialsContainEmailPassword ||
      !currentAccessToken;

    // Un-awaited and deliberately ahead of the access check: AppPage's first
    // paint waits on these flags, so the request has to overlap the rest of
    // the bootstrap instead of queueing behind it.
    void featureFlags.loadBackendFlags();

    if (shouldLoadData) {
      const outcome = await userState.loadAppAccess();
      if (!this.isCurrentBootstrap(generation)) {
        return;
      }
      await appAccessState.evaluateAccess(
        outcome.subscriptionsLoaded,
        outcome.profileLoaded
      );
      if (!this.isCurrentBootstrap(generation)) {
        return;
      }
      userState.loadDeferredUserData();
    }

    // Re-check WebSocket connection after app access evaluation
    await this.recheckWebSocketConnection();
  }

  private dispatchStateChangeEvents(
    authenticationStatus: string,
    previousUserId: string | null,
    newUserId: string | null,
    persistedUserIdBeforeUpdate: string | null
  ): void {
    // Notify when authenticated and user changed
    if (
      authenticationStatus === "authenticated" &&
      newUserId !== previousUserId
    ) {
      this.log("Dispatching SET_CREDENTIALS for state clearing", {
        previousUserId,
        newUserId
      });
      window.dispatchEvent(new CustomEvent(globalEvents.SET_CREDENTIALS));
    }

    // Dispatch USER_CHANGED only when the user ID actually changes from a known
    // user. The previous user is whoever the last access token belonged to, and
    // failing that whoever storage was scoped to — a session restored from
    // storage has no previous token to read the outgoing user from.
    const previousKnownUserId = previousUserId || persistedUserIdBeforeUpdate;
    if (
      authenticationStatus === "authenticated" &&
      newUserId &&
      previousKnownUserId &&
      newUserId !== previousKnownUserId
    ) {
      this.log("Dispatching USER_CHANGED - user actually changed", {
        previousKnownUserId,
        newUserId
      });
      window.dispatchEvent(
        new CustomEvent(globalEvents.USER_CHANGED, {
          detail: { previousUserId: previousKnownUserId, newUserId }
        })
      );
    }
  }

  private async handleEmailPasswordLogin(): Promise<void> {
    // Mark session verified if this was a biometric fallback login
    biometricAuthState.markSessionVerifiedAfterEmailLogin();

    // Show biometric setup prompt after login with email/password
    // Only show if biometric is available but not yet enabled
    const biometricAvailable =
      biometricAuthState.availableOnDevice &&
      (await biometricAuthState.isAvailable()).isAvailable;
    const biometricEnabled =
      biometricAuthState.state.isBiometricVerificationEnabled;

    if (biometricAvailable && !biometricEnabled) {
      this.log("Showing biometric setup prompt after login");
      void biometricAuthState.openSetupPrompt();
    }
  }

  async deleteTokenCredentials(): Promise<void> {
    this.log("deleteTokenCredentials");
    await this.setCredentials({
      accessToken: undefined,
      refreshToken: undefined
    });

    try {
      await this.tokenProvider.deleteCredentials();
    } catch (error) {
      console.error("Failed to delete token credentials", error);
      reportErrorSentry(
        new Error("Failed to delete token credentials", { cause: error })
      );
    }
  }

  async deleteCredentials(): Promise<void> {
    this.log("deleteCredentials");
    await this.setCredentials({
      accessToken: undefined,
      refreshToken: undefined,
      username: undefined,
      password: undefined
    });

    try {
      await this.tokenProvider.deleteCredentials();
    } catch (error) {
      console.error("Failed to delete credentials", error);
    }
  }

  public readonly login = async (data: {
    email: string;
    password: string;
    mfa?: string;
  }): Promise<void> => {
    if (this.loginRequest) {
      this.log("login: already running, returning in-flight request");
      return this.loginRequest;
    }

    const request = (async () => {
      const loadingState = Blac.getBloc(LoadingCubit);
      loadingState.start(LoadingKey.login);
      try {
        const response = await UserControllerService.login({
          ...data,
          role: LoginRequest.role.USER,
          sessionName: navigator.userAgent,
          mfaToken: data.mfa
        });

        await this.setCredentials({
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          username: data.email,
          password: data.password
        });

        this.log("Authenticated user");
        logSentryEvent("auth.login.success");
      } catch (e: unknown) {
        reportAuthError("login", e);
        throw e;
      } finally {
        loadingState.finish(LoadingKey.login);
      }
    })();

    this.loginRequest = request;
    try {
      await request;
    } finally {
      this.loginRequest = undefined;
    }
  };

  public readonly createAnonymousAccount = async (): Promise<void> => {
    // Deduplication - return existing promise if in-flight
    if (this.createAnonymousAccountRequest) {
      this.log("createAnonymousAccount: already running");
      return this.createAnonymousAccountRequest;
    }

    // Set deduplication promise BEFORE any async operations
    const request = (async () => {
      try {
        if (this.refreshToken) {
          return;
        }

        // Safety net - check storage directly in case state isn't updated yet
        const storageRefreshToken = StorageController.getItem("refreshToken");
        if (storageRefreshToken) {
          this.log(
            "createAnonymousAccount: refresh token found in storage, skipping"
          );
          return;
        }

        // Wait for initial credentials to finish loading before deciding to create anonymous account
        if (this.initialCredentialsRequest) {
          this.log("createAnonymousAccount: waiting for credentials to load");
          await this.initialCredentialsRequest;
          // Re-check after credentials loaded
          if (this.refreshToken) {
            this.log(
              "createAnonymousAccount: refresh token found after loading, skipping"
            );
            return;
          }
        }

        if (this.state.authenticationStatus !== "unauthenticated") {
          reportAuthError(
            "anonymous",
            new Error("Creating anonymous account while already authenticated"),
            {
              step: "already_authenticated",
              authStatus: this.state.authenticationStatus
            }
          );
        }

        const { data } = await UserControllerService.registerAnonymously();
        this.log("Created anonymous account");
        await this.setCredentials({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken
        });
      } catch (e: unknown) {
        reportAuthError("anonymous", e, {
          authStatus: this.state.authenticationStatus
        });
      } finally {
        this.createAnonymousAccountRequest = undefined;
      }
    })();

    this.createAnonymousAccountRequest = request;
    return request;
  };

  public readonly register = async (
    data: {
      email: string;
      password: string;
    },
    funnel?: string
  ): Promise<void> => {
    let error: ApiError | undefined;
    let loginSuccess = false;

    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.register);

    // try to log in user first in case where the account already exists
    try {
      await this.login(data);
      loginSuccess = true;
    } catch (e) {
      const loginError = e as ApiError;
      const errorBody = loginError.body as
        | { code?: number | string }
        | undefined;
      const errorCode = errorBody?.code ? String(errorBody.code) : "";

      if (errorCode === ErrorCode.emailVerificationRequired) {
        this.log("Login failed: email verification required");
        throw loginError;
      }

      console.info("Failed to login user");
      reportAuthError("register", e, { step: "login" });
    }

    if (!loginSuccess) {
      try {
        const registerResponse = await UserControllerService.register({
          ...data,
          initiateEmailVerification: true
        });

        // Store verification token if returned
        if (registerResponse.data.verificationToken) {
          userEmailVerificationState.setVerificationToken(
            registerResponse.data.verificationToken
          );
          userEmailVerificationState.storeCooldownStart();
        }

        await userState.acceptConsents();
        await this.setCredentials({
          username: data.email,
          password: data.password,
          accessToken: this.accessToken,
          refreshToken: this.refreshToken
        });

        const userPreferences = Blac.getBloc(UserPreferencesCubit);
        await userPreferences.updateUserPreferences({
          [UserPreferenceKeys.registerFunnel]: funnel
        });
        apiMiddleware.clearAll();
      } catch (e: unknown) {
        error = e as ApiError;
      }
    }

    loadingState.finish(LoadingKey.register);
    if (error) {
      reportAuthError("register", error, { step: "create" });
      this.log("Failed to register user");
      throw error;
    }
  };

  public readonly resetPassword = async (postData: {
    email: string;
  }): Promise<ResetPasswordResponse> => {
    let error: ApiError | undefined;
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.requestPassReset);
    let response: ResetPasswordResponse | undefined;

    try {
      const { data } =
        await UserControllerService.initiatePasswordReset(postData);
      response = data;
      this.log("Successfully reset password");
    } catch (e: unknown) {
      error = e as ApiError;
      this.log("Failed to reset password");
    }

    loadingState.finish(LoadingKey.requestPassReset);

    if (error) {
      reportAuthError("password_reset", error);
      throw error;
    }
    if (!response) {
      throw new Error("No response");
    }

    return response;
  };

  public readonly changePassword = async (
    data: ChangePasswordRequest
  ): Promise<void> => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.changePassword);
    let error: ApiError | undefined;

    try {
      await UserControllerService.changePassword(data);
      this.log("Successfully changed password");
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = e as ApiError;
    }
    loadingState.finish(LoadingKey.changePassword);

    if (error) {
      throw error;
    }
  };

  public readonly changeEmail = async (
    changeEmailRequest: ChangeEmailRequest
  ): Promise<ChangeEmailResponse> => {
    apiMiddleware.clearAll();
    let response: ChangeEmailResponse | undefined;
    let error: ApiError | undefined;
    try {
      const { data } =
        await UserControllerService.changeEmail(changeEmailRequest);
      response = data;
      this.log("Successfully changed email");
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = e as ApiError;
    }
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.finish(LoadingKey.verifyEmail);

    if (error) {
      throw error;
    }
    if (!response) {
      throw new Error("No response");
    }
    return response;
  };

  public readonly resetPasswordVerify = async (
    formData: ResetPasswordConfirmRequest
  ): Promise<void> => {
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.updatePass);
    let error: ApiError | undefined;
    try {
      await UserControllerService.confirmPasswordReset(formData);
      this.log("Successfully changed password");
    } catch (e: unknown) {
      reportErrorSentry(e);
      error = e as ApiError;
    }
    loadingState.finish(LoadingKey.updatePass);

    if (error) {
      throw error;
    }
  };

  public readonly logout = async (): Promise<void> => {
    const hasToken = Boolean(this.accessToken);
    BlockingLoadingOverlayController.startLoading({
      bg: "branded"
    });

    if (hasToken) {
      try {
        await healthSyncState.unregisterDevice();
      } catch (e) {
        reportAuthError("logout", e, { step: "unregister_device" });
      }
      try {
        await pushNotificationState.unregister();
      } catch (e) {
        reportAuthError("logout", e, { step: "unregister_push" });
      }
      try {
        await UserControllerService.logout();
      } catch (e) {
        reportAuthError("logout", e, { step: "logout_api" });
      }
      try {
        await this.tokenProvider.deleteCredentials();
      } catch (e) {
        reportAuthError("logout", e, { step: "delete_token_credentials" });
      }
      websocketState.disconnect();
      void this.deleteCredentials();
      void this.clearAllUserData();
    }

    BlockingLoadingOverlayController.endLoading();
  };

  clearAllUserData = async (): Promise<void> => {
    this.log("clearAllUserData - starting", {
      previousSelectedUserId: this.selectedUserId,
      previousActiveUserId: this.activeUserId
    });

    const userPreferences = Blac.getBloc(UserPreferencesCubit);
    tracker.resetMixpanelUser();
    // Dispatched while the user ID is still set: the file cache and every
    // storage-backed reset it triggers are scoped to the user being cleared.
    window.dispatchEvent(new CustomEvent(globalEvents.USER_CLEAR));
    userState.reset();
    userPreferences.reset();
    subscriptionState.reset();
    this.selectedUserId = null;
    this.updateStorageActiveUserId();
    this.log("clearAllUserData - completed");
  };

  reportFaultyAccessToken = async (): Promise<void> => {
    this.log("reportFaultyAccessToken");
    return this.refreshAccessToken();
  };

  sendHandoverTokenLilly = async (options: {
    token: string;
    type: PopulateUserRequest.type;
  }): Promise<void> => {
    const { token, type } = options;
    try {
      await LillyControllerService.populateUser({ token, type });
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  sendHandoverTokenSolera = async (options: {
    token: string;
  }): Promise<void> => {
    const { token } = options;
    try {
      await SoleraControllerService.populateSoleraUser({ enrollmentId: token });
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  authWithSamlToken = async (token: string): Promise<void> => {
    try {
      const appViewState = Blac.getBloc(AppViewCubit);
      const decodedToken = parseJwt(token);
      const reg = decodedToken.samlreg ?? "";
      appViewState.setPartnerSource(reg);
    } catch (e) {
      reportErrorSentry(e);
    }

    let authData: Awaited<
      ReturnType<typeof Saml2AuthControllerService.exchange>
    >;
    try {
      authData = await Saml2AuthControllerService.exchange({ token });
    } catch (e) {
      // prepareForExternalTokenLogin has already wiped the session and set this
      // flag. Leaving it set on a keepAlive singleton disables token refresh and
      // session restore until a full page reload, so release it before rethrowing.
      this.externalLoginInProgress = false;
      const decodedReg = (() => {
        try {
          return parseJwt(token).samlreg ?? "";
        } catch {
          return "";
        }
      })();
      reportAuthError("saml", e, { partnerReg: decodedReg });
      throw e;
    }
    this.externalLoginInProgress = false;

    await this.setCredentials({
      accessToken: authData.data.accessToken,
      refreshToken: authData.data.refreshToken
    });
  };

  /**
   * Prepare for external token-based login (OTT / SAML).
   * Hard reset timers, inflight refresh, websocket, auth state and storage.
   */
  prepareForExternalTokenLogin = (): void => {
    this.log("prepareForExternalTokenLogin");
    this.externalLoginInProgress = true;

    // Invalidate any in-flight bootstrap from the session being replaced, so
    // a stale loadAppAccess() resolution can't pass isCurrentBootstrap() and
    // act on the cleared session.
    this.bootstrapGeneration++;

    // Stop auto-refresh timer
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = undefined;
    }

    // Nullify any inflight requests
    AuthenticationBloc.refreshAccessTokenRequest = undefined;
    this.createAnonymousAccountRequest = undefined;
    this.initialCredentialsRequest = undefined;

    this.emit({
      ...this.state,
      authenticationStatus: "unauthenticated",
      credentials: {},
      authStage: "nullAuth"
    });

    // Clear all users across storages and file cache
    try {
      StorageController.clearAllUsers();
      void DbFileService.clearAll();
    } catch (e) {
      reportErrorSentry(e);
    }
  };

  updateAuthStage = async (): Promise<void> => {
    if (this.updateAuthStageRequest) {
      return this.updateAuthStageRequest;
    }

    const request = this.runUpdateAuthStage();
    this.updateAuthStageRequest = request;
    try {
      await request;
    } finally {
      this.updateAuthStageRequest = undefined;
    }
  };

  private readonly runUpdateAuthStage = async (): Promise<void> => {
    if (!this.accessToken) {
      this.emit({ ...this.state, authStage: "noAuth" });
      return;
    }

    await userState.loadUserInfo();
    const { authenticationStatus } = this;
    const { userData } = userState.state;

    function detectAuthStage(): AuthStage {
      if (
        authenticationStatus === "authenticated" &&
        userData &&
        userData.email === undefined &&
        userData.passwordSet === false
      ) {
        return "anonymous";
      } else if (
        authenticationStatus === "authenticated" &&
        userData &&
        (userData.email === undefined || userData.passwordSet === false)
      ) {
        if (userData.email === undefined) {
          return "partialAuth";
        }

        // If the user has a sessionAuthenticationMethod, we consider it fullAuth, does not need password
        const authMethod = userData.sessionAuthenticationMethod;
        if (
          authMethod === AuthenticationMethod.TRANSCARENT_SSO ||
          authMethod === AuthenticationMethod.SAML2
        ) {
          return "fullAuth";
        }
      } else if (authenticationStatus === "authenticated" && userData) {
        return "fullAuth";
      }
      return "noAuth";
    }

    const authStage = detectAuthStage();

    StorageController.setAuthStage(authStage);

    this.emit({ ...this.state, authStage });

    // Re-check WebSocket connection after auth stage changes
    await this.recheckWebSocketConnection();
  };
}
